import { lazy } from 'react';
import { CreateInvoiceGroupModal } from '@rideiq/organizers/features/InvoiceGroups/CreateInvoiceGroupModal';
import { DeleteInvoiceGroupModal } from '@rideiq/organizers/features/InvoiceGroups/DeleteInvoiceGroupModal';
import { EditInvoiceGroupModal } from '@rideiq/organizers/features/InvoiceGroups/EditInvoiceGroupModal';
import { BillingInvoiceGroups } from '@rideiq/organizers/pages/BillingInvoiceGroups';

import ChangePassword from '../auth/pages/ChangePassword';
import ForgotPassword from '../auth/pages/ForgotPassword';
import Login from '../auth/pages/Login';
import SignUp from '../auth/pages/SignUp';
import SsoCallback from '../auth/pages/SsoCallback';
import Calendars from '../calendars/pages/Calendars';
import { actions, subjects } from '../constants/abilities';
import { LocationDetailsLayout } from '../locations/components/LocationDetailsLayout';
import RiderInstructions from '../locations/pages/RiderInstructions';
import { EditOrganizerProfile } from '../organizers/features/EditOrganizer';
import { NewOrganizer } from '../organizers/features/NewOrganizer';
import Settings, { OrganizerList, SettingsForm } from '../organizers/pages';
import RecordingsPage from '../recordings/pages/RecordingsPage';
import { ReportsLayout } from '../reports/components/ReportsLayout';
import { CustomReportModal } from '../reports/features/CustomReportModal';
import { RiderFormEntry, RiderFormEntryEmbedded } from '../riders/features/RiderForm/RiderFormEntry';
import RiderCentricDetails from '../riders/pages/RiderCentricDetails';
import Riders from '../riders/pages/Riders';
import { ShareAccess } from '../rides/features/DailyRides/ShareAccess/ShareAccess';
import AddRide from '../rides/pages/AddRide';
import { RemovedRiders } from '../rides/pages/RemovedRiders';
import Rides from '../rides/pages/Rides';
import { RidesViewsLayout } from '../rides/pages/Rides/RidesViewsLayout';

import { componentLoader } from './componentLoader';
import { PrivateRouteConfig, PublicRouteConfig } from './types';

const lazyLoad = (fn) => lazy(() => componentLoader(fn, 4, 1500));

const AllLocations = lazyLoad(() => import('../locations/pages/AllLocations'));
const AddLocation = lazyLoad(() => import('../locations/pages/AddLocation'));
const EditLocation = lazyLoad(() => import('../locations/pages/EditLocation'));
const LocationDetails = lazyLoad(() => import('../locations/pages/LocationDetails'));
const CalendarLayout = lazyLoad(() =>
  import('../calendars/components/CalendarLayout').then((module) => ({ default: module.CalendarLayout }))
);
const CalendarDetails = lazyLoad(() => import('../calendars/pages/CalendarDetails'));
const Reports = lazyLoad(() => import('../reports/pages/ReportsDashboard'));
const ReportsDownloads = lazyLoad(() => import('../reports/pages/Downloads'));
const RiderSummary = lazyLoad(() => import('../reports/pages/RiderSummary'));
const RiderSummaryHistory = lazyLoad(() => import('../reports/pages/RiderSummaryHistory'));
const OrganizerDetails = lazyLoad(() => import('../organizers/pages/OrganizerDetails'));
const BillingDocuments = lazyLoad(() => import('../organizers/pages/BillingDocuments'));
const BillingFolderFiles = lazyLoad(() => import('../organizers/pages/BillingDocuments/BillingFolderFiles'));
const PauseRiderModal = lazyLoad(() => import('../riders/features/PauseRider/PauseRiderModal'));
const UnpauseRiderModal = lazyLoad(() => import('../riders/features/UnpauseRider/UnpauseRiderModal'));
const DailyRides = lazyLoad(() => import('../rides/pages/DailyRides'));
const RideDetails = lazyLoad(() => import('../rides/pages/RideDetails'));
const EditRideDetails = lazyLoad(() => import('../rides/pages/EditRideDetails'));
const RideDuplication = lazyLoad(() => import('../rides/pages/RideDuplication'));
const OnsiteStaffList = lazyLoad(() => import('../organizers/pages/OnsiteStaffList'));
const AddOnsiteStaff = lazyLoad(() => import('../organizers/pages/AddOnsiteStaff'));
const SharedRiderRequest = lazyLoad(() => import('../auth/pages/SharedRiderRequest'));
const SchoolDetails = lazyLoad(() => import('../locations/pages/SchoolDetails'));
const Schools = lazyLoad(() => import('../locations/pages/Schools'));
const LocationsBatchUploads = lazyLoad(() => import('../locations/pages/BatchUploads'));
const RiderInstructionsDetails = lazyLoad(() => import('../locations/pages/RiderInstructionsDetails'));
const CalendarAssignedLocations = lazyLoad(() => import('../calendars/pages/CalendarAssignedLocations'));

const RidesWithProvider = Rides;

const publicRoutes: PublicRouteConfig[] = [
  {
    path: '/login',
    title: 'Login',
    component: Login,
  },
  {
    path: '/organizers/welcome',
    title: 'Sign Up',
    component: SignUp,
  },
  {
    path: '/forgotPassword',
    title: 'Forgot Password',
    component: ForgotPassword,
  },
  {
    path: '/organizers/password/edit',
    title: 'Change Password',
    component: ChangePassword,
  },
  {
    path: '/sso/callback',
    title: 'SSO Callback',
    component: SsoCallback,
  },
  {
    path: 'passengers/shared_billing/share_request',
    title: 'Shared Rider Request',
    component: SharedRiderRequest,
  },
];

const privateRoutes: PrivateRouteConfig[] = [
  {
    path: '/rides',
    component: RidesViewsLayout,
    title: 'Rides: All Rides',
    allowedAction: actions.listView,
    allowedSubject: subjects.ride,
    nestedRoutes: [
      {
        index: true,
        component: RidesWithProvider,
        allowedAction: actions.listView,
        allowedSubject: subjects.ride,
      },
      {
        path: 'removed_riders',
        component: RemovedRiders,
        title: 'Rides: Removed Riders',
        allowedAction: actions.read,
        allowedSubject: subjects.ride,
      },
    ],
  },
  {
    path: '/rides/new',
    component: AddRide,
    title: 'Create Ride',
    allowedAction: actions.all,
    allowedSubject: subjects.ride,
    nestedRoutes: [
      {
        path: 'riders/new',
        component: RiderFormEntryEmbedded,
        allowedAction: actions.add,
        allowedSubject: subjects.rider,
      },
    ],
  },
  {
    path: '/rideseries/new',
    component: AddRide,
    title: 'Create Ride Series',
    allowedAction: actions.all,
    allowedSubject: subjects.ride,
  },
  {
    path: '/rides/duplicate',
    component: RideDuplication,
    title: 'Duplicate Ride',
    allowedAction: actions.edit,
    allowedSubject: subjects.ride,
  },
  {
    path: '/rides/:rideId',
    component: RideDetails,
    title: 'Ride Details',
    allowedAction: actions.read,
    allowedSubject: subjects.ride,
  },
  {
    path: '/rides/:rideId/edit',
    component: EditRideDetails,
    title: 'Edit Ride',
    allowedAction: actions.all,
    allowedSubject: subjects.ride,
    nestedRoutes: [
      {
        path: 'riders/new',
        component: RiderFormEntryEmbedded,
        allowedAction: actions.add,
        allowedSubject: subjects.rider,
      },
    ],
  },
  {
    path: '/rides/daily_queue',
    component: DailyRides,
    title: 'Rides: Daily Queue',
    allowedAction: actions.listView,
    allowedSubject: subjects.ride,
    nestedRoutes: [
      {
        path: 'share_access',
        component: ShareAccess,
        allowedAction: actions.add,
        allowedSubject: subjects.onsiteStaff,
      },
    ],
  },
  {
    path: '/riders',
    component: Riders,
    title: 'Riders',
    allowedAction: actions.listView,
    allowedSubject: subjects.rider,
  },
  {
    path: '/riders/new',
    component: RiderFormEntry,
    title: 'Create Rider',
    allowedAction: actions.add,
    allowedSubject: subjects.rider,
  },
  {
    path: '/riders/:slug/edit',
    component: RiderFormEntry,
    title: 'Edit Rider',
    allowedAction: actions.edit,
    allowedSubject: subjects.rider,
  },
  {
    path: '/riders/:riderId',
    component: RiderCentricDetails,
    title: 'Rider Details',
    allowedAction: actions.read,
    allowedSubject: subjects.rider,
    nestedRoutes: [
      {
        path: 'pause_rider',
        component: PauseRiderModal,
        allowedAction: actions.read,
        allowedSubject: subjects.rider,
      },
      {
        path: 'unpause_rider',
        component: UnpauseRiderModal,
        allowedAction: actions.read,
        allowedSubject: subjects.rider,
      },
    ],
  },
  {
    path: '/locations',
    component: AllLocations,
    title: 'Locations: All Locations',
    allowedAction: actions.listView,
    allowedSubject: subjects.location,
  },
  {
    path: '/locations/new',
    component: AddLocation,
    title: 'New Location',
    allowedAction: actions.add,
    allowedSubject: subjects.location,
  },
  {
    path: '/locations/:locationId',
    component: LocationDetailsLayout,
    selfGuarded: true,
    nestedRoutes: [
      {
        path: '',
        component: LocationDetails,
        title: 'Location Details',
        allowedAction: actions.read,
        allowedSubject: subjects.location,
      },
      {
        path: 'rider_instructions',
        component: RiderInstructions,
        title: 'Rider Instructions',
        allowedAction: actions.read,
        allowedSubject: subjects.rider,
      },
    ],
  },
  {
    path: '/locations/:locationId/edit',
    component: EditLocation,
    title: 'Edit Location',
    allowedAction: actions.edit,
    allowedSubject: subjects.location,
  },
  {
    path: '/locations/:locationId/rider_instructions/:riderId',
    component: RiderInstructionsDetails,
    title: 'Rider Instructions Details',
    allowedAction: actions.read,
    allowedSubject: subjects.rider,
  },
  {
    path: '/locations/schools',
    component: Schools,
    title: 'Locations: Schools',
    allowedAction: actions.listView,
    allowedSubject: subjects.organizer,
  },
  {
    path: '/locations/batch_uploads',
    component: LocationsBatchUploads,
    title: 'Locations: Batch Uploads',
    allowedAction: actions.listView,
    allowedSubject: subjects.location,
  },
  {
    path: '/locations/schools/:schoolId',
    component: SchoolDetails,
    title: 'School Location Details',
    allowedAction: actions.read,
    allowedSubject: subjects.organizer,
  },
  {
    path: '/calendars',
    component: Calendars,
    title: 'Calendars',
    allowedAction: actions.listView,
    allowedSubject: subjects.calendar,
  },
  {
    path: '/calendars/new',
    component: CalendarDetails,
    title: 'New Calendar',
    allowedAction: actions.add,
    allowedSubject: subjects.calendar,
  },
  {
    path: '/calendars/:calendarId',
    component: CalendarLayout,
    title: 'Calendar Details',
    allowedAction: actions.read,
    allowedSubject: subjects.calendar,
    nestedRoutes: [
      {
        path: '',
        component: CalendarDetails,
        title: 'Calendar Details',
        allowedAction: actions.read,
        allowedSubject: subjects.calendar,
      },
      {
        path: 'assigned_locations',
        component: CalendarAssignedLocations,
        title: 'Calendar: Assigned Locations',
        allowedAction: actions.read,
        allowedSubject: subjects.calendar,
      },
    ],
  },
  {
    path: '/recordings',
    component: RecordingsPage,
    title: 'Ride Recordings',
    allowedAction: actions.listView,
    allowedSubject: subjects.recording,
  },
  {
    path: '/reports/downloads',
    component: ReportsDownloads,
    title: 'Reports: Downloads',
    allowedAction: actions.read,
    allowedSubject: subjects.dashboard,
  },
  {
    path: '/reports',
    component: ReportsLayout,
    title: 'Reports: All Rides',
    selfGuarded: true,
    nestedRoutes: [
      {
        path: 'riders',
        component: RiderSummary,
        title: 'Reports: Riders Information',
        allowedAction: actions.all,
        allowedSubject: subjects.clientReport,
      },
      {
        path: 'history',
        component: RiderSummaryHistory,
        title: 'Reports: Report History',
        allowedAction: actions.all,
        allowedSubject: subjects.clientReport,
      },
      {
        path: '',
        component: Reports,
        title: 'Reports: All Rides',
        allowedAction: actions.read,
        allowedSubject: subjects.dashboard,
        nestedRoutes: [
          {
            path: 'modal',
            component: CustomReportModal,
            allowedAction: actions.read,
            allowedSubject: subjects.dashboard,
          },
        ],
      },
      {
        path: ':id',
        component: Reports,
        allowedAction: actions.read,
        allowedSubject: subjects.dashboard,
        nestedRoutes: [
          {
            path: 'modal',
            component: CustomReportModal,
            allowedAction: actions.read,
            allowedSubject: subjects.dashboard,
          },
        ],
      },
    ],
  },
  {
    path: '/settings',
    component: Settings,
    title: 'Account Settings',
    allowedAction: actions.read,
    allowedSubject: subjects.settings,
    nestedRoutes: [
      {
        path: 'account_details',
        title: 'Account Settings: Account Details',
        component: SettingsForm,
        allowedAction: actions.read,
        allowedSubject: subjects.settings,
      },
      {
        path: 'organizers',
        component: OrganizerList,
        title: 'Account Settings: Organizers',
        allowedAction: actions.read,
        allowedSubject: subjects.settings,
        nestedRoutes: [
          {
            path: 'new',
            component: NewOrganizer,
            title: 'Account Settings: New Organizer',
            allowedAction: actions.read,
            allowedSubject: subjects.organizer,
          },
        ],
      },
      {
        path: 'onsite_staff',
        component: OnsiteStaffList,
        title: 'Account Settings: Onsite Staffs',
        allowedAction: actions.read,
        allowedSubject: subjects.settings,
        nestedRoutes: [
          {
            path: 'new',
            component: AddOnsiteStaff,
            title: 'Account Settings: New Onsite Staff',
            allowedAction: actions.read,
            allowedSubject: subjects.organizer,
          },
        ],
      },
      {
        path: 'billing_documents',
        component: BillingDocuments,
        title: 'Account Settings: Billing Documents',
        allowedAction: actions.read,
        allowedSubject: subjects.billingDocument,
        nestedRoutes: [
          {
            path: ':folderKey',
            component: BillingFolderFiles,
            title: 'Account Settings: Billing Documents',
            allowedAction: actions.read,
            allowedSubject: subjects.billingDocument,
          },
        ],
      },
      {
        path: 'invoice_groups',
        component: BillingInvoiceGroups,
        title: 'Account Settings: Invoice Groups',
        allowedAction: actions.read,
        allowedSubject: subjects.settings,
        nestedRoutes: [
          {
            path: 'new',
            component: CreateInvoiceGroupModal,
            title: 'Account Settings: New Invoice Group',
            allowedAction: actions.read,
            allowedSubject: subjects.organizer,
          },
          {
            path: ':id/edit',
            component: EditInvoiceGroupModal,
            title: 'Account Settings: Edit Invoice Group',
            allowedAction: actions.read,
            allowedSubject: subjects.organizer,
          },
          {
            path: ':id/delete',
            component: DeleteInvoiceGroupModal,
            title: 'Account Settings: Delete Invoice Group',
            allowedAction: actions.read,
            allowedSubject: subjects.organizer,
          },
        ],
      },
    ],
  },
  {
    path: '/settings/organizers/:uuid',
    component: OrganizerDetails,
    title: 'Account Settings: Organizer Details',
    allowedAction: actions.read,
    allowedSubject: subjects.organizer,
    nestedRoutes: [
      {
        path: 'edit',
        component: EditOrganizerProfile,
        title: 'Account Settings: Edit Organizer',
        allowedAction: actions.read,
        allowedSubject: subjects.organizer,
      },
    ],
  },
];

const onsiteStaffRoutes: PrivateRouteConfig[] = [
  {
    path: '/rides/daily_queue',
    component: DailyRides,
    title: 'Rides: Daily Queue',
    allowedAction: actions.listView,
    allowedSubject: subjects.ride,
    nestedRoutes: [
      {
        path: 'share_access',
        component: ShareAccess,
        allowedAction: actions.add,
        allowedSubject: subjects.onsiteStaff,
      },
    ],
  },
  {
    path: '/rides/:rideId',
    component: RideDetails,
    title: 'Ride Details',
    allowedAction: actions.read,
    allowedSubject: subjects.ride,
  },
];

export { privateRoutes, publicRoutes, onsiteStaffRoutes };
